import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { InformStage } from "./InformStage";
import { DecideStage } from "./DecideStage";
import { ExecuteStage } from "./ExecuteStage";
import { AssessStage } from "./AssessStage";
import { SprintCollaborationHeader } from "./SprintCollaborationHeader";

import { Lightbulb, Vote, Zap, Star } from "lucide-react";

interface Sprint {
  id: string;
  objective: string;
  inform_stage_completed: boolean;
  decide_stage_completed: boolean;
  execute_stage_completed: boolean;
  assess_stage_completed: boolean;
}

interface SprintStageManagerProps {
  sprint: Sprint;
  userId: string;
  onStageComplete: (stage: string) => void;
}

const stages = [
  { id: 'inform', name: 'Inform', icon: Lightbulb, color: 'text-blue-500' },
  { id: 'decide', name: 'Decide', icon: Vote, color: 'text-purple-500' },
  { id: 'execute', name: 'Execute', icon: Zap, color: 'text-orange-500' },
  { id: 'assess', name: 'Assess & Sustain', icon: Star, color: 'text-green-500' },
];

export const SprintStageManager = ({ sprint, userId, onStageComplete }: SprintStageManagerProps) => {
  const [currentStage, setCurrentStage] = useState(0);

  useEffect(() => {
    // Determine current stage based on completion status
    if (!sprint.inform_stage_completed) {
      setCurrentStage(0);
    } else if (!sprint.decide_stage_completed) {
      setCurrentStage(1);
    } else if (!sprint.execute_stage_completed) {
      setCurrentStage(2);
    } else if (!sprint.assess_stage_completed) {
      setCurrentStage(3);
    } else {
      setCurrentStage(4); // All complete
    }
  }, [sprint]);

  const progress = ((currentStage) / 4) * 100;

  const handleStageComplete = (stageName: string) => {
    onStageComplete(stageName);
    setCurrentStage(prev => Math.min(prev + 1, 4));
  };

  return (
    <div className="space-y-6">
      {/* Sprint Collaboration Header */}
      <SprintCollaborationHeader sprintId={sprint.id} />
      
      {/* Compact Progress Header */}
      <Card className="p-4">
        <div className="space-y-3">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-foreground">Sprint Objective: {sprint.objective}</h2>
          </div>
          
          <div className="flex items-center justify-between">
            <Progress value={progress} className="h-2 flex-1 mr-4" />
            <div className="text-sm text-muted-foreground whitespace-nowrap">
              Stage {currentStage + 1} of 4
            </div>
          </div>
          
          {/* Stage Breadcrumbs */}
          <div className="flex items-center justify-between gap-2">
            {stages.map((stage, index) => {
              const Icon = stage.icon;
              const isActive = index === currentStage;
              const isComplete = index < currentStage;
              
              return (
                <div
                  key={stage.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary/10 border border-primary'
                      : isComplete
                      ? 'bg-muted opacity-60'
                      : 'bg-muted/30 opacity-40'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? stage.color : 'text-muted-foreground'}`} />
                  <span className={`text-sm font-medium ${isActive ? '' : 'text-muted-foreground'}`}>
                    {stage.name}
                  </span>
                  {isComplete && <span className="text-green-500">✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Current Stage Content */}
      <div className="animate-fade-in">
        {currentStage === 0 && (
          <InformStage
            sprintId={sprint.id}
            userId={userId}
            onComplete={() => handleStageComplete('inform')}
          />
        )}
        
        {currentStage === 1 && (
          <DecideStage
            sprintId={sprint.id}
            userId={userId}
            onComplete={() => handleStageComplete('decide')}
          />
        )}
        
        {currentStage === 2 && (
          <ExecuteStage
            sprintId={sprint.id}
            userId={userId}
            onComplete={() => handleStageComplete('execute')}
          />
        )}
        
        {currentStage === 3 && (
          <AssessStage
            sprintId={sprint.id}
            userId={userId}
            onComplete={() => handleStageComplete('assess')}
          />
        )}

        {currentStage === 4 && (
          <Card className="p-8 text-center">
            <div className="space-y-4">
              <div className="text-6xl">🎉</div>
              <h3 className="text-2xl font-bold">Sprint Complete!</h3>
              <p className="text-muted-foreground">
                Congratulations! Your prompt architecture sprint is finished.
                Check your updated GibRating in the dashboard.
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
